/**
 * Sep 03, 2026 · Permission Reference Guide
 *
 * A static, in-app cheat-sheet for the 11 Core Permission Categories
 * used across the MES. Rendered under Reports & System so operators
 * and administrators can look up the meaning of a permission bucket
 * without leaving the app.
 *
 * The 11 categories mirror the functional split baked into the
 * `_LEGACY_MODULE_MAP` (server.py + auth.jsx) and the sidebar's six
 * top-level groups — Sales & Dispatch, Exports, Production, Purchase,
 * Master Data, Reports — expanded to eleven so each family lines up
 * cleanly with the RBAC grid on `/settings/roles`.
 */
import React, { useMemo, useState } from "react";
import {
  ShoppingBagIcon, GlobeIcon, TruckIcon, GearIcon, FactoryIcon,
  ShoppingCartIcon, StackIcon, ShieldCheckIcon, DatabaseIcon,
  ChartBarIcon, LockKeyIcon, MagnifyingGlassIcon,
} from "@phosphor-icons/react";

// ── 11 Core Permission Categories ────────────────────────────────────
// Each row is a distinct functional domain in the MES. `actions` lists
// the standard 8-verb matrix (view / create / edit / delete / approve /
// unapprove / print / export) — a category may not need every action
// but the grid on `/settings/roles` always ships all 8 checkboxes for
// consistency.
const ALL_ACTIONS = ["view", "create", "edit", "delete", "approve", "unapprove", "print", "export"];

const CATEGORIES = [
  {
    key: "orders",
    icon: ShoppingBagIcon,
    accent: "border-l-sky-500",
    dot: "bg-sky-400",
    label: "Orders",
    scope: "Sales Orders, Proforma Invoices",
    purpose: "Create, amend and dispatch every domestic sales order plus the PIs that seed them.",
    modules: ["sales_orders", "proforma_invoices"],
    actions: ALL_ACTIONS,
    notes: "Approve/unapprove is the workflow gate between draft and Live SO.",
  },
  {
    key: "exports",
    icon: GlobeIcon,
    accent: "border-l-blue-500",
    dot: "bg-blue-400",
    label: "Exports",
    scope: "Export Sales Orders, Export PIs, ESO reconciliation",
    purpose: "Everything under Exports & Dispatch — Export Orders, PLs, DCs, GST Invoices, ESO reports.",
    modules: ["exports", "exports_epl", "exports_dc", "exports_invoices", "export_gst_invoices", "export_proforma_invoices", "reports_eso_reconciliation", "reports_eso_dispatch_history"],
    actions: ALL_ACTIONS,
    notes: "Kept separate from Orders because export operators rarely touch domestic flows and vice versa.",
  },
  {
    key: "dispatch",
    icon: TruckIcon,
    accent: "border-l-teal-500",
    dot: "bg-teal-400",
    label: "Dispatch",
    scope: "Packing Slips, Packing Lists, Delivery Challans, GST Invoices",
    purpose: "The three-stage domestic dispatch chain — pack, list, deliver — plus tax invoicing at the tail.",
    modules: ["sales_orders_slips", "sales_orders_packing", "sales_orders_challans", "gst_invoices"],
    actions: ALL_ACTIONS,
    notes: "Legacy shorthand `dispatch:*` is aliased to a grant on any of the three sub-modules.",
  },
  {
    key: "planning",
    icon: GearIcon,
    accent: "border-l-emerald-500",
    dot: "bg-emerald-400",
    label: "Planning",
    scope: "Production Planning, Work Orders, Child WOs, Material Requisitions",
    purpose: "Weekly / monthly plans, machine allocation, WO breakdown, and material issue requests.",
    modules: ["production_planning", "production_orders", "child_work_orders", "material_requisitions", "material_issue"],
    actions: ALL_ACTIONS,
    notes: "Legacy shorthand `planning:*` maps to `production_planning`.",
  },
  {
    key: "production",
    icon: FactoryIcon,
    accent: "border-l-lime-500",
    dot: "bg-lime-400",
    label: "Production",
    scope: "Weaving, Preparatory, Department Entry, Downtime, Rejections",
    purpose: "Live shop-floor capture — daily production, machine downtime, wastage, and stage transfers.",
    modules: ["weaving", "entry", "preparatory_entry", "downtime", "rejections", "stage_transfer"],
    actions: ALL_ACTIONS,
    notes: "Legacy shorthand `production:*` maps to `weaving` (the umbrella prod module).",
  },
  {
    key: "quality",
    icon: ShieldCheckIcon,
    accent: "border-l-green-500",
    dot: "bg-green-400",
    label: "Quality",
    scope: "QC Approval, Batch Traceability, Defects, Rejections",
    purpose: "Post-production inspection, batch trace lookup, and defect / rejection sign-off.",
    modules: ["quality", "qc_approval", "quality_traceability"],
    actions: ALL_ACTIONS,
    notes: "Approve/unapprove drives the QC gate between production and dispatch.",
  },
  {
    key: "purchase",
    icon: ShoppingCartIcon,
    accent: "border-l-amber-500",
    dot: "bg-amber-400",
    label: "Purchase",
    scope: "Purchase Requirements, POs, GRNs, Purchase Returns",
    purpose: "Every raw-material and consumable procurement flow up to receipt into stores.",
    modules: ["purchase_orders", "purchase_requirements", "inventory_purchase_receipt", "inventory_purchase_returns"],
    actions: ALL_ACTIONS,
    notes: "Approve/unapprove is the release gate before the PO is sent to the supplier.",
  },
  {
    key: "inventory",
    icon: StackIcon,
    accent: "border-l-orange-500",
    dot: "bg-orange-400",
    label: "Inventory",
    scope: "Stock Ledger, Bin Transfers, Reservations, Scrap, Physical Verification",
    purpose: "Everything that moves stock — internal transfers, adjustments, and physical counts.",
    modules: ["inventory", "stock_register", "inventory_bin_transfer", "inventory_reservations", "inventory_scrap", "inventory_physical_verification", "inventory_opening_stock", "inventory_returnable_challans", "inventory_excess", "inventory_covering_rm_transfer", "inventory_stock_book", "inventory_low_stock"],
    actions: ALL_ACTIONS,
    notes: "Physical verification and scrap flows require the `approve` action to lock the count.",
  },
  {
    key: "masters",
    icon: DatabaseIcon,
    accent: "border-l-violet-500",
    dot: "bg-violet-400",
    label: "Master Data",
    scope: "FG, SF, RM, BOM, Customers, Suppliers, Machines, HSN, UOM, Currencies, FX",
    purpose: "All reference/lookup data. Edits here ripple through every downstream document.",
    modules: ["articles", "sf_master", "bom", "raw_materials", "masters_customers", "masters_suppliers", "masters_machines", "masters_items", "masters_hsn_codes", "masters_uom_masters", "masters_currencies", "masters_fx_rates", "masters_tax_masters"],
    actions: ALL_ACTIONS,
    notes: "Deleting master rows is blocked when transactions reference them — soft-delete only via SF-split tombstoning.",
  },
  {
    key: "reports",
    icon: ChartBarIcon,
    accent: "border-l-cyan-500",
    dot: "bg-cyan-400",
    label: "Reports & Dashboards",
    scope: "Sales, Purchase, Inventory Dashboards, Reconciliation Reports, Reversal Audit, Live Monitor",
    purpose: "Read-mostly analytics. `export` action controls CSV/PDF downloads.",
    modules: ["reports", "sales_dashboard", "dashboards_purchase", "dashboards_inventory", "live", "reports_reversal_audit", "reports_dispatch_history", "reports_pending_sales_order", "reports_pending_purchase_order", "reports_so_reconciliation", "reports_rm_wastage_summary"],
    actions: ["view", "print", "export"],
    notes: "Read-heavy — only view/print/export are wired for most rows; other actions are inert.",
  },
  {
    key: "system_rbac",
    icon: LockKeyIcon,
    accent: "border-l-rose-500",
    dot: "bg-rose-400",
    label: "System & RBAC",
    scope: "Users & Roles, Roles & Permissions, Role Master, Activity Trail, Company Profile",
    purpose: "Administrator-only surface. Grants here are what mint every other role's grants.",
    modules: ["masters_users", "settings_roles", "masters_roles", "audit_log", "settings_company", "settings_tnc_templates", "management_approvals"],
    actions: ALL_ACTIONS,
    notes: "Administrator role bypasses every check; this bucket exists so a delegated Sys-Admin role can still be built without granting full super-user rights.",
  },
];

const ACTION_LEGEND = [
  { verb: "view",      hint: "Open the screen / read rows"                                  },
  { verb: "create",    hint: "Add a new record"                                             },
  { verb: "edit",      hint: "Amend an existing record"                                     },
  { verb: "delete",    hint: "Hard-delete a record (blocked when downstream links exist)"   },
  { verb: "approve",   hint: "Move a record forward in its workflow (Draft → Live, etc.)"  },
  { verb: "unapprove", hint: "Reverse an approval and drop the record back to Draft"       },
  { verb: "print",     hint: "Open the PDF preview / send to printer"                       },
  { verb: "export",    hint: "Download the row-set as CSV / Excel"                          },
];

function ActionBadge({ verb, active }) {
  const cls = active
    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
    : "bg-zinc-800/60 text-zinc-600 border-zinc-800 line-through";
  return (
    <span data-testid={`action-badge-${verb}`} className={`inline-flex items-center px-1.5 py-[1px] rounded-sm text-[10px] uppercase tracking-wider border ${cls}`}>
      {verb}
    </span>
  );
}

export default function PermissionReferenceGuide() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CATEGORIES;
    return CATEGORIES.filter(c =>
      c.label.toLowerCase().includes(needle) ||
      c.scope.toLowerCase().includes(needle) ||
      c.purpose.toLowerCase().includes(needle) ||
      c.modules.some(m => m.toLowerCase().includes(needle))
    );
  }, [q]);

  return (
    <div data-testid="permission-reference-page" className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Reports & System</div>
          <h1 className="text-xl font-semibold text-zinc-100 mt-0.5">Permission Reference Guide</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            The 11 Core Permission Categories used across the MES.
            Cross-reference this table against the checkbox grid on
            {" "}<span className="text-zinc-300">Roles &amp; Permissions</span>{" "}
            to decide which actions a new role needs.
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="perm-ref-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Filter category / module / keyword…"
            className="w-64 bg-zinc-900/80 border border-zinc-800 pl-7 pr-2 py-1.5 rounded text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600"
          />
        </div>
      </div>

      {/* Phase 3J.22A-R2 · Bulk Reset Transactions endpoint permanently
          retired. Posted transactions must now be reversed via the
          applicable document workflow — see Reversal Audit. This banner
          is preserved as an admin-facing notice so anyone still scripting
          against the old endpoint learns immediately that it is Gone. */}
      <div
        data-testid="perm-ref-danger-banner"
        className="rounded-md border border-zinc-700/60 bg-zinc-950/40 border-l-2 border-l-zinc-500 px-3.5 py-2.5 flex gap-3 items-start"
      >
        <div className="h-8 w-8 rounded-sm bg-zinc-900/60 flex items-center justify-center flex-shrink-0">
          <LockKeyIcon size={16} className="text-zinc-300" />
        </div>
        <div className="min-w-0 flex-1 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-400" />
            <span className="text-sm font-medium text-zinc-100">Retired: Bulk Reset Transactions</span>
            <span className="text-[10px] px-1.5 py-[1px] rounded-sm border border-zinc-500/60 text-zinc-300 font-mono uppercase tracking-widest">Phase 3J.22A-R2</span>
          </div>
          <p className="text-[11px] text-zinc-300 mt-1">
            <code className="text-zinc-200">POST /api/admin/reset-transactions</code> and{" "}
            <code className="text-zinc-200">POST /api/admin/system-reset</code> are permanently retired
            and now return HTTP 410 Gone. Posted transactions must be corrected via the applicable
            per-document reversal / cancellation workflow.
          </p>
          <p className="text-[11px] text-zinc-400 mt-1">
            See Reversal Audit for the canonical reversal paths (GRN Delete, MI reversal, Transfer
            reversal, CRT reversal, Production reversal, WTR reversal, DC/EDC Delivery reversal,
            Purchase Return).
          </p>
        </div>
      </div>

      {/* Action legend */}
      <div data-testid="perm-ref-legend" className="rounded-md border border-zinc-800/80 bg-zinc-950/40 p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Action Verbs (8-column matrix)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 text-[11px]">
          {ACTION_LEGEND.map(({ verb, hint }) => (
            <div key={verb} className="flex items-baseline gap-2">
              <ActionBadge verb={verb} active />
              <span className="text-zinc-500">{hint}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-2.5">
        {filtered.map((c, idx) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              data-testid={`perm-ref-row-${c.key}`}
              className={`rounded-md border border-zinc-800/80 bg-zinc-950/40 border-l-2 ${c.accent} px-3.5 py-2.5`}
            >
              <div className="flex items-start gap-3">
                <div className={`h-8 w-8 rounded-sm bg-zinc-900/80 flex items-center justify-center flex-shrink-0`}>
                  <Icon size={16} className="text-zinc-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${c.dot}`} />
                    <span className="text-[10px] text-zinc-600 font-mono">{String(idx + 1).padStart(2, "0")}</span>
                    <span className="text-sm font-medium text-zinc-100">{c.label}</span>
                    <span className="text-[10px] text-zinc-500">·</span>
                    <span className="text-[11px] text-zinc-500">{c.scope}</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">{c.purpose}</p>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {ALL_ACTIONS.map(a => (
                      <ActionBadge key={a} verb={a} active={c.actions.includes(a)} />
                    ))}
                  </div>
                  <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 mt-2.5">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-0.5">Backend module codes</div>
                      <div className="flex flex-wrap gap-1">
                        {c.modules.map(m => (
                          <code key={m} className="text-[10px] bg-zinc-900/80 border border-zinc-800 px-1.5 py-[1px] rounded-sm text-zinc-400">
                            {m}
                          </code>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-0.5">Notes</div>
                      <p className="text-[11px] text-zinc-500">{c.notes}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div data-testid="perm-ref-empty" className="text-center py-8 text-xs text-zinc-500">
            No category matches "{q}"
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pt-4 mt-4 border-t border-zinc-900 text-[11px] text-zinc-500 space-y-1">
        <div>
          <span className="text-zinc-400">Cross-reference:</span>{" "}
          Every module code above appears in the checkbox grid at
          {" "}<a href="/settings/roles" data-testid="perm-ref-link-rbac" className="text-sky-400 hover:underline">Roles &amp; Permissions</a>.
        </div>
        <div>
          <span className="text-zinc-400">Legacy shorthands:</span>{" "}
          <code className="text-zinc-400">dispatch:*</code>, <code className="text-zinc-400">packing:*</code>, <code className="text-zinc-400">orders:*</code>, <code className="text-zinc-400">planning:*</code>, <code className="text-zinc-400">production:*</code>{" "}
          are auto-aliased to their granular modules server-side (see <code>server.py::_LEGACY_MODULE_MAP</code>).
        </div>
      </div>
    </div>
  );
}
